import React, { useEffect, useState, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { NiaStats } from '../../types/admin';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import SiteLayout from '../../layouts/SiteLayout';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { Bot, Zap, MessageSquare, Users, Database, Search, RefreshCw, Trash2, TrendingUp, AlertTriangle } from 'lucide-react';

const COLORS = ['#6366f1', '#8b5cf6', '#d946ef', '#f43f5e', '#f59e0b', '#10b981'];

const AdminNiaDashboard: React.FC = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState<NiaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReindexing, setIsReindexing] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/v1/admin/rag/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching Nia stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000); // 1 minute auto-refresh
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleReindex = async () => {
    if (!window.confirm("Lancer une ré-indexation complète ? Cela consommera des ressources serveurs pendant plusieurs minutes.")) return;

    setIsReindexing(true);
    try {
      const res = await fetch(`${API_URL}/v1/admin/rag/reindex`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setActionMessage({ text: data.message, type: 'success' });
      } else {
        setActionMessage({ text: "Erreur lors du lancement.", type: 'error' });
      }
    } catch (error) {
      setActionMessage({ text: "Erreur réseau.", type: 'error' });
    } finally {
      setIsReindexing(false);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm("Vider le cache sémantique ? Nia devra recalculer ses réponses aux questions déjà posées.")) return;

    try {
      const res = await fetch(`${API_URL}/v1/admin/rag/cache`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setActionMessage({ text: "Cache sémantique vidé avec succès.", type: 'success' });
        fetchStats();
      }
    } catch (error) {
      setActionMessage({ text: "Erreur réseau.", type: 'error' });
    } finally {
      setTimeout(() => setActionMessage(null), 3000);
    }
  };

  const hitRate = stats ? (stats.total_messages > 0 ? (stats.cache_hits / (stats.total_messages + stats.cache_hits)) * 100 : 0) : 0;

  const pieData = stats ? Object.entries(stats.source_distribution).map(([name, value]) => ({ name, value })) : [];

  if (loading && !stats) {
    return (
      <SiteLayout>
        <Header icon='psychology' title='Dashboard Adoption Nia' />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div className="loader"></div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Header icon='psychology' title='Dashboard Adoption Nia' />

      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>

        {actionMessage && (
          <div style={{
            padding: '15px',
            borderRadius: '12px',
            marginBottom: '20px',
            backgroundColor: actionMessage.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            color: actionMessage.type === 'success' ? '#10b981' : '#ef4444',
            border: `1px solid ${actionMessage.type === 'success' ? '#10b981' : '#ef4444'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            {actionMessage.type === 'success' ? <RefreshCw size={20} /> : <AlertTriangle size={20} />}
            {actionMessage.text}
          </div>
        )}

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <Box>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ padding: '10px', borderRadius: '10px', background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1' }}>
                  <Database size={24} />
                </div>
                <div style={{ color: stats?.total_chunks ? '#10b981' : '#ef4444', fontSize: '12px', fontWeight: 600 }}>
                  {stats && stats.total_chunks > 3000 ? 'SANTÉ : OK' : 'SANTÉ : CRITIQUE'}
                </div>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-heading)' }}>{stats?.total_chunks.toLocaleString()}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Fragments de Connaissance (RAG)</div>
            </div>
          </Box>

          <Box>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ padding: '10px', borderRadius: '10px', background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                  <MessageSquare size={24} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#10b981', fontSize: '13px', fontWeight: 600 }}>
                  <TrendingUp size={14} /> +12%
                </div>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-heading)' }}>{stats?.total_messages.toLocaleString()}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Messages de discussion Nia</div>
            </div>
          </Box>

          <Box>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ padding: '10px', borderRadius: '10px', background: 'rgba(217, 70, 239, 0.1)', color: '#d946ef' }}>
                  <Users size={24} />
                </div>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-heading)' }}>{stats?.total_ai_users.toLocaleString()}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Utilisateurs actifs de l'IA</div>
            </div>
          </Box>

          <Box>
            <div style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ padding: '10px', borderRadius: '10px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                  <Zap size={24} />
                </div>
                <div style={{ background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                  EFFICIENCE
                </div>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-heading)' }}>{hitRate.toFixed(1)}%</div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Taux de Hit Cache Sémantique</div>
            </div>
          </Box>
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '30px' }}>
          <Box>
            <div style={{ padding: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <TrendingUp size={20} /> Tendance d'utilisation (30 derniers jours)
              </h3>
              <div style={{ width: '100%', height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats?.usage_trend}>
                    <defs>
                      <linearGradient id="colorMsg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#666' }} minTickGap={30} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#666' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', background: 'white' }}
                    />
                    <Area type="monotone" dataKey="messages" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorMsg)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Box>

          <Box>
            <div style={{ padding: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>Sources RAG</h3>
              <div style={{ width: '100%', height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" align="center" iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Box>
        </div>

        {/* Bottom Section: Queries and Controls */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '20px' }}>
          <Box>
            <div style={{ padding: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Search size={20} /> Intentions Populaires (Top 10)
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                    <th style={{ textAlign: 'left', padding: '12px', color: 'var(--text-muted)', fontSize: '13px' }}>Question</th>
                    <th style={{ textAlign: 'right', padding: '12px', color: 'var(--text-muted)', fontSize: '13px' }}>Fréquence</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.top_queries.map((q, i) => (
                    <tr key={i} style={{ borderBottom: i === stats.top_queries.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.03)' }}>
                      <td style={{ padding: '12px', fontSize: '14px', fontWeight: 500 }}>"{q.query}"</td>
                      <td style={{ textAlign: 'right', padding: '12px' }}>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: '20px',
                          backgroundColor: 'rgba(99, 102, 241, 0.1)',
                          color: '#6366f1',
                          fontSize: '12px',
                          fontWeight: 700
                        }}>
                          {q.hits} hits
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!stats?.top_queries || stats.top_queries.length === 0) && (
                    <tr><td colSpan={2} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Aucune donnée de cache disponible.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Box>

          <Box>
            <div style={{ padding: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>Contrôle AI Engine</h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <button
                  onClick={handleReindex}
                  disabled={isReindexing}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    padding: '16px',
                    borderRadius: '16px',
                    background: 'var(--theme-gradient)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    fontWeight: 700,
                    cursor: isReindexing ? 'not-allowed' : 'pointer',
                    opacity: isReindexing ? 0.7 : 1,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                  }}
                >
                  <RefreshCw size={20} className={isReindexing ? 'spin' : ''} />
                  {isReindexing ? 'Ré-indexation...' : 'Lancer Ré-indexation'}
                </button>

                <button
                  onClick={handleClearCache}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    padding: '16px',
                    borderRadius: '16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Trash2 size={20} />
                  Vider Cache Sémantique
                </button>

                <div style={{
                  marginTop: '10px',
                  padding: '15px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  fontSize: '12px',
                  color: 'var(--text-muted)',
                  lineHeight: '1.6'
                }}>
                  <strong>Note technique :</strong>
                  <br />- Ré-indexation : Recrée les fragments (Financial Chunks) à partir de la BD.
                  <br />- Cache sémantique : Stocke les réponses Gemini pour économiser les tokens.
                </div>
              </div>
            </div>
          </Box>
        </div>
      </div>

      <style>{`
        .spin {
          animation: spin 2s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </SiteLayout>
  );
};

export default AdminNiaDashboard;
